import { useEffect, useMemo, type CSSProperties, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, CircularProgress, useTheme } from "@mui/material";
import { listSuggestions } from "../api/ess";
import { displayDepartment } from "../api/departments";
import { useDepartments } from "../hooks/useDepartments";
import { useWorkflowConfig } from "../hooks/useWorkflow";
import { unitGroupLabel } from "../utils/departments";
import { formatDateTime } from "../utils/format";
import { inferPeriodLabel } from "../utils/period";
import {
  REPORT_BUCKETS,
  buildOverviewReport,
  buildTrend,
  describeReportScope,
  parseOverviewPrintParams,
} from "./reports/utils";

// Document-style Overview report — same approach as SuggestionPrint.
// No MUI Cards / shadows / chips — just sectioned text with horizontal
// rules so the printed output reads as an industrial report rather than
// a UI dashboard. The Overview's Print button serialises its whole filter
// set into the URL (buildOverviewPrintUrl); this route parses it back,
// lists the same rows and runs the same report model, so every figure
// equals the screen and the XLSX.

// Ink scale for the printed report. Paper is not a themed surface — these
// are hairlines, dashed rules and captions picked for a laser printer, not
// screen tokens — so they stay raw and opt out of the `lint:tokens` gate
// line by line. The one brand-bearing value (the trend bar fill) reads
// `primary.dark` off the theme at its callsite instead.
const PRINT_INK = {
  rule: "#d6d6d6", // allow-hex
  ruleWarn: "#d6b6b6", // allow-hex
  inkWarn: "#a14040", // allow-hex
  caption: "#666", // allow-hex
  barTrack: "#eee", // allow-hex
} as const;

// Leaderboards print the same top slice the screen shows before "Show more".
const PRINT_LEADERBOARD_LIMIT = 10;

const CELL: CSSProperties = { padding: "3pt 0" };
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "11pt",
};

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
}) {
  return (
    <div>
      <div className="print-field-label">{label}</div>
      <div
        className="print-field-value"
        style={{ fontSize: "16pt", fontWeight: 600, marginTop: "1pt" }}
      >
        {value}
      </div>
      {sub && (
        <div className="print-meta" style={{ marginTop: "1pt" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

interface PrintColumn {
  header: string;
  align?: "left" | "right" | "center";
  width?: string;
}

function PrintTable({
  columns,
  rows,
  fontSize,
}: {
  columns: PrintColumn[];
  rows: ReactNode[][];
  fontSize?: string;
}) {
  return (
    <table style={{ ...TABLE, ...(fontSize && { fontSize }) }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.header}
              style={{
                ...CELL,
                textAlign: c.align ?? "left",
                borderBottom: `1px solid ${PRINT_INK.rule}`,
                fontWeight: 600,
                width: c.width,
              }}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, i) => (
          <tr key={i} className="print-block">
            {cells.map((cell, j) => (
              <td
                key={j}
                style={{
                  ...CELL,
                  textAlign: columns[j]?.align ?? "left",
                  paddingRight: j < cells.length - 1 ? "6pt" : 0,
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const pct = (n: number, of: number) =>
  `${of > 0 ? Math.round((n * 100) / of) : 0}%`;

/** A "Label · Count · Share" breakdown, the shape every By-X section prints. */
function Breakdown({
  title,
  label,
  rows,
  total,
  highlight,
}: {
  title: string;
  label: string;
  rows: [string, number][];
  total: number;
  highlight?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h2 className="print-section">{title}</h2>
      {highlight && (
        <div className="print-meta" style={{ marginBottom: "4pt" }}>
          {highlight}
        </div>
      )}
      <PrintTable
        columns={[
          { header: label },
          { header: "Count", align: "right" },
          { header: "Share", align: "right", width: "80pt" },
        ]}
        rows={rows.map(([k, n]) => [k, n, pct(n, total)])}
      />
    </section>
  );
}

function Leaderboard({
  title,
  caption,
  rows,
}: {
  title: string;
  caption: string;
  rows: [string, ReactNode][];
}) {
  return (
    <div className="print-block">
      <div className="print-field-label">{title}</div>
      <div className="print-meta" style={{ marginBottom: "2pt" }}>
        {caption}
      </div>
      {rows.length === 0 ? (
        <div className="print-meta">None in this period.</div>
      ) : (
        <PrintTable
          fontSize="10pt"
          columns={[
            { header: "#", width: "18pt" },
            { header: "Name" },
            { header: "", align: "right", width: "44pt" },
          ]}
          rows={rows
            .slice(0, PRINT_LEADERBOARD_LIMIT)
            .map(([name, value], i) => [`${i + 1}.`, name, value])}
        />
      )}
    </div>
  );
}

export default function OverviewPrint() {
  const theme = useTheme();
  const [params] = useSearchParams();
  const filters = useMemo(() => parseOverviewPrintParams(params), [params]);

  const { data: workflow, isLoading: workflowLoading } = useWorkflowConfig();

  // Same department -> unit lookup the Overview builds: it drives the Unit
  // filter and the Top Units board, so the list waits for it rather than
  // running twice and printing the first answer.
  const departmentsQuery = useDepartments();
  const departmentsSettled =
    departmentsQuery.isSuccess || departmentsQuery.isError;
  const unitByDepartment = useMemo(() => {
    const m: Record<string, string> = {};
    (departmentsQuery.data ?? []).forEach((d) => {
      if (d?.name) m[d.name] = unitGroupLabel(d);
    });
    return m;
  }, [departmentsQuery.data]);

  const list = useQuery({
    queryKey: ["ess-list-print", filters, unitByDepartment],
    queryFn: () => listSuggestions(filters, unitByDepartment),
    enabled: departmentsSettled,
  });

  const allRows = useMemo(() => list.data?.suggestions ?? [], [list.data]);
  const report = useMemo(
    () => buildOverviewReport(allRows, workflow, unitByDepartment),
    [allRows, workflow, unitByDepartment],
  );
  const trend = useMemo(
    () => buildTrend(allRows, filters.date_from, filters.date_to),
    [allRows, filters.date_from, filters.date_to],
  );

  const ready = !workflowLoading && list.isSuccess;
  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(t);
  }, [ready]);

  if (list.isError) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Could not load Overview data.{" "}
          {list.error instanceof Error ? list.error.message : ""}
        </Alert>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const {
    total,
    closed,
    rejected,
    closureRate,
    rejectRate,
    medianCloseDays,
    agingCount,
    committee,
    topDepartment,
  } = report;
  const periodLabel = inferPeriodLabel(filters.date_from, filters.date_to);
  const scope = describeReportScope(filters, workflow);
  const trendMax = Math.max(1, ...trend.buckets.map((b) => b.count));
  const trendGranularityLabel: Record<string, string> = {
    day: "by day",
    week: "by week",
    month: "by month",
    quarter: "by quarter",
  };
  const rejectedLane = report.rejections.filter((b) => b.count > 0);

  return (
    <div className="print-root">
      <div className="print-page">
        <header className="print-header">
          <img
            src="/images/indorama/logo_dark.webp"
            alt="Indorama"
            style={{
              height: "32pt",
              marginBottom: "8pt",
              marginLeft: "-6pt",
              display: "block",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16pt",
            }}
          >
            <div>
              <div className="print-subhead">Overview Report</div>
              <h1 className="print-title">Suggestion Scheme — {periodLabel}</h1>
              <div className="print-meta">
                {filters.date_from && filters.date_to
                  ? `${filters.date_from} → ${filters.date_to}`
                  : "All available data"}
              </div>
              {scope.map((line) => (
                <div key={line} className="print-meta">
                  {line}
                </div>
              ))}
            </div>
            <div className="print-meta" style={{ whiteSpace: "nowrap" }}>
              Printed {formatDateTime(new Date().toISOString())}
            </div>
          </div>
        </header>

        <section className="print-block">
          <h2 className="print-section">Headline Metrics</h2>
          <div className="print-grid">
            <Stat label="Total submissions" value={total} />
            <Stat
              label="Closure rate"
              value={`${closureRate}%`}
              sub={`${closed} of ${total} closed`}
            />
            <Stat
              label="Median time to close"
              value={
                medianCloseDays === null
                  ? "—"
                  : medianCloseDays === 0
                    ? "<1 day"
                    : `${medianCloseDays} days`
              }
              sub={
                closed === 0
                  ? "no closed suggestions"
                  : `across ${closed} closed`
              }
            />
            <Stat
              label="Aging (parked > 14 days)"
              value={agingCount}
              sub="non-terminal, last status change > 14d ago"
            />
            <Stat
              label="SLA breaches"
              value={committee.slaBreaches}
              sub="past the 90-day window"
            />
            <Stat
              label="Reject rate"
              value={total > 0 ? `${rejectRate}%` : "—"}
              sub={
                rejectedLane.length > 0
                  ? `${rejected} rejected (${rejectedLane
                      .map((b) => `${b.count} ${b.label}`)
                      .join(", ")})`
                  : `${rejected} rejected`
              }
            />
          </div>
        </section>

        <section className="print-block">
          <h2 className="print-section">Pipeline · Cumulative Reach</h2>
          <table style={TABLE}>
            <thead>
              <tr>
                {["Stage", "Count", "% of Submitted"].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      ...CELL,
                      textAlign: i === 0 ? "left" : "right",
                      borderBottom: `1px solid ${PRINT_INK.rule}`,
                      fontWeight: 600,
                      width: i === 2 ? "100pt" : undefined,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.pipeline.map((s) => (
                <tr key={s.state} className="print-block">
                  <td style={CELL}>{s.label}</td>
                  <td style={{ ...CELL, textAlign: "right" }}>{s.count}</td>
                  <td style={{ ...CELL, textAlign: "right" }}>
                    {pct(s.count, total)}
                  </td>
                </tr>
              ))}
              <tr className="print-block">
                {[
                  "Rejected (lane)",
                  rejected,
                  pct(rejected, total),
                ].map((cell, i) => (
                  <td
                    key={i}
                    style={{
                      ...CELL,
                      textAlign: i === 0 ? "left" : "right",
                      borderTop: `1px dashed ${PRINT_INK.ruleWarn}`,
                      color: PRINT_INK.inkWarn,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="print-meta" style={{ marginTop: "4pt" }}>
            Each stage = suggestions that ever reached this point, including
            ones rejected further on.
          </div>
        </section>

        <section>
          <h2 className="print-section">Committee Report</h2>
          <div className="print-grid print-block">
            {REPORT_BUCKETS.map((b) => (
              <Stat key={b.key} label={b.label} value={committee.buckets[b.key]} />
            ))}
            <Stat label="SLA breaches" value={committee.slaBreaches} />
          </div>
          <div
            className="print-grid"
            style={{ marginTop: "10pt", alignItems: "start" }}
          >
            <Leaderboard
              title="Best suggestions"
              caption="SC rating"
              rows={committee.scEvaluations.map((r) => [
                r.suggestionNumber
                  ? `${r.suggestionNumber} · ${r.title}`
                  : r.title,
                `${r.rating}%`,
              ])}
            />
            <Leaderboard
              title="Most approved per person"
              caption="Approved by SC"
              rows={committee.mostApprovedPerPerson.map((p) => [
                `${p.name} (${p.employeeId})`,
                p.approved,
              ])}
            />
            <Leaderboard
              title="Top units"
              caption="Approved by SC"
              rows={committee.topUnits.map((u) => [u.unit, u.approved])}
            />
            <Leaderboard
              title="Top departments"
              caption="Suggestions received"
              rows={committee.topDepartments.map((d) => [
                displayDepartment(d.department),
                d.count,
              ])}
            />
          </div>
        </section>

        {committee.scEvaluations.length > 0 && (
          <section>
            <h2 className="print-section">SC Evaluations</h2>
            <PrintTable
              fontSize="9.5pt"
              columns={[
                { header: "S/N", align: "right", width: "24pt" },
                { header: "Originator" },
                { header: "Employee ID" },
                { header: "Suggestion No" },
                { header: "Department" },
                { header: "SC rating %", align: "right" },
                { header: "Reward category", align: "center" },
                { header: "Remark" },
              ]}
              rows={committee.scEvaluations.map((r, i) => [
                i + 1,
                r.originator,
                r.employeeId,
                r.suggestionNumber || "—",
                r.department,
                `${r.rating}%`,
                r.rewardCategory || "—",
                r.remark,
              ])}
            />
          </section>
        )}

        {trend.buckets.length > 0 && (
          <section className="print-block">
            <h2 className="print-section">
              Submissions Trend{" "}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: "10pt",
                  color: PRINT_INK.caption,
                  marginLeft: "8pt",
                }}
              >
                {trendGranularityLabel[trend.granularity]}
              </span>
            </h2>
            <PrintTable
              fontSize="10.5pt"
              columns={[
                { header: "Period", width: "90pt" },
                { header: "Volume" },
                { header: "Count", align: "right", width: "60pt" },
              ]}
              rows={trend.buckets.map((b) => [
                b.label,
                <div
                  style={{
                    background: PRINT_INK.barTrack,
                    borderRadius: 2,
                    height: 6,
                    margin: "0 8pt",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      background: theme.palette.primary.dark,
                      height: "100%",
                      width: `${(b.count / trendMax) * 100}%`,
                      borderRadius: 2,
                    }}
                  />
                </div>,
                b.count,
              ])}
            />
          </section>
        )}

        <Breakdown
          title="By Status"
          label="Status"
          rows={report.byStatus.map((r) => [r.label, r.count])}
          total={total}
        />
        <Breakdown
          title="By Department"
          label="Department"
          rows={report.byDepartment.map(([d, n]) => [displayDepartment(d), n])}
          total={total}
          highlight={
            topDepartment
              ? `Highest: ${displayDepartment(topDepartment[0])} · ${
                  topDepartment[1]
                } ${topDepartment[1] === 1 ? "suggestion" : "suggestions"}`
              : undefined
          }
        />
        <Breakdown
          title="By Category"
          label="Category"
          rows={report.byCategory}
          total={total}
        />
        <Breakdown
          title="By Reward Category"
          label="Reward category"
          rows={report.byReward.map((r) => [r.key, r.count])}
          total={report.rewardTotal}
        />
      </div>
    </div>
  );
}
